import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { http } from 'app/lib/axios';
import { asFormData } from 'app/lib/form';
import type { Bonification } from 'app/types/bonification';
import type { ImagePickerAsset } from 'expo-image-picker';
import { bonificationKeys } from 'app/network/keys';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import { useQueryLPC } from 'app/lib/query';
import { useAllListings } from './listing';
import { useQueryKeys } from './invalidation';
import { type Profile } from 'app/types/profile';
import { getMyListingsCountFilters, getMyListingsOffersFilters } from './listing-query-filters';
import { isLoggedInAtom } from 'app/atoms';
import { useAtomValue } from 'jotai';
import { ListingType } from 'app/types/listing';

interface CreateBonificationInput {
  bonificationId?: Bonification['id'];
  loanId?: string;
  suggestedTitle?: string;
  suggestedDescription?: string;
  picture?: ImagePickerAsset;
  channelUrl?: string;
}

interface AcceptBonificationInput {
  id: Bonification['id'];
  channelUrl?: string;
  suggestedTitle?: string;
  suggestedDescription?: string;
  picture?: ImagePickerAsset;
}

interface RejectBonificationInput {
  id: Bonification['id'];
  channelUrl?: string;
}

export function useCreateBonification(
  opts: UseMutationOptions<Bonification | undefined, Error, CreateBonificationInput> = {},
) {
  const qk = useQueryKeys();
  return useMutation<Bonification | undefined, Error, CreateBonificationInput>({
    mutationFn: (input) => {
      if (!input.bonificationId && !input.loanId) {
        throw new Error('Missing bonificationId and loanId');
      }
      return http.post<Bonification>('/bonifications', asFormData(input)).then((res) => {
        void qk.activeChannels.invalidate();
        if (res.data.id) {
          void qk.afterBonificationCreated(input.channelUrl);
        }
        return res.data;
      });
    },
    ...opts,
  });
}

export function useAcceptBonification(opts: UseMutationOptions<Bonification, Error, AcceptBonificationInput> = {}) {
  const qk = useQueryKeys();
  return useMutation<Bonification, Error, AcceptBonificationInput>({
    mutationFn: ({ id, channelUrl, ...modifications }) => {
      const hasModifications =
        modifications.suggestedTitle !== undefined ||
        modifications.suggestedDescription !== undefined ||
        modifications.picture !== undefined;
      if (!hasModifications) {
        throw new Error('At least one modification must be provided to accept a bonification');
      }
      const body = asFormData(modifications);
      return http.post<Bonification>(`/bonifications/${encodeURIComponent(id)}/accept`, body).then((res) => {
        void qk.afterBonificationAccepted(id, res.data.listingId, channelUrl);
        return res.data;
      });
    },
    ...opts,
  });
}

export function useRejectBonification(
  opts: UseMutationOptions<Bonification | undefined, Error, RejectBonificationInput> = {},
) {
  const qk = useQueryKeys();
  return useMutation<Bonification | undefined, Error, RejectBonificationInput>({
    mutationFn: ({ id, channelUrl }) => {
      return http.post<Bonification>(`/bonifications/${encodeURIComponent(id)}/reject`).then((res) => {
        void qk.afterBonificationRejected(id, channelUrl);
        return res.data;
      });
    },
    ...opts,
  });
}

export function useOneBonification(id?: Bonification['id'], opts: FunctionalUseQueryOptions<Bonification> = {}) {
  return useQueryLPC<Bonification>({
    queryKey: bonificationKeys.detail(id!),
    queryFn: () => http.get<Bonification>(`/bonifications/${encodeURIComponent(id!)}`).then((res) => res.data),
    ...opts,
    enabled: (typeof opts.enabled === 'boolean' ? opts.enabled : true) && !!id,
  });
}

type BonificationWithListing = Bonification & {
  listing: { item: { name: string }; picture: string };
  suggestedByUser: { profile: Profile };
};
export function usePendingBonifications(opts: FunctionalUseQueryOptions<BonificationWithListing[]> = {}) {
  return useQueryLPC<BonificationWithListing[]>({
    queryKey: bonificationKeys.pending(),
    queryFn: () => http.get<BonificationWithListing[]>(`/bonifications/pending`).then((res) => res.data),
    ...opts,
    enabled: typeof opts.enabled === 'boolean' ? opts.enabled : true,
  });
}

export function useListingsToBonify({
  userId,
  enabled = true,
  loadFirstBonifiableListing = false,
  triggeredBy = 'ListingsToBonify',
}: {
  userId?: string;
  enabled?: boolean;
  /** When true, loads enough listings to resolve the first bonifiable item (e.g. single-tap navigation). */
  loadFirstBonifiableListing?: boolean;
  triggeredBy?: string;
}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  const countQuery = useAllListings(getMyListingsCountFilters(isLoggedIn, userId, ListingType.offer), triggeredBy, {
    throwOnError: true,
    enabled,
    populateListingDetailCache: false,
  });

  const numberOfListingsToBonify = enabled ? (countQuery.data?.pages[0]?.numberOfBonifiableListings ?? 0) : 0;
  const isFullQueryEnabled = enabled && loadFirstBonifiableListing && numberOfListingsToBonify === 1;

  const fullListingsQuery = useAllListings(getMyListingsOffersFilters(isLoggedIn, userId), `${triggeredBy}-full`, {
    throwOnError: true,
    enabled: isFullQueryEnabled,
    populateListingDetailCache: true,
  });

  const listingsQuery = isFullQueryEnabled ? fullListingsQuery : countQuery;
  const offers = enabled ? listingsQuery.data : undefined;
  const refetchOffers = async () => {
    if (!enabled) {
      return;
    }

    return listingsQuery.refetch();
  };

  const firstBonifiableListing = (() => {
    if (!offers) return null;
    return offers.pages.flatMap((page) => page.results).find((listing) => listing.canBonify) ?? null;
  })();

  return {
    firstBonifiableListing,
    firstBonifiableListingNotFound: firstBonifiableListing === null && numberOfListingsToBonify > 0,
    numberOfListingsToBonify,
    refetchOffers,
    offers,
    isPending: enabled ? countQuery.isPending || (isFullQueryEnabled && fullListingsQuery.isPending) : false,
  };
}
